import { useAuth } from "./auth";
import { get } from "./AureaApi";
import { getUrl } from "../getUrl";
import { ApiCustomer } from "./customer";
import { setLogsCustomer } from "../logs";
import {
  FeatureCollection,
  Geometry,
  MultiPolygon as GeoJsonMultiPolygon,
  Polygon as GeoJsonPolygon,
} from "geojson";
import { ActionFunctionArgs } from "react-router";
import { MessageLevel } from "../Component/message-flash/MessageFlash";
import { TaskmapHistoryEntry } from "./taskmapConfigHistory";
import type { ResultSetCalibration } from "./calibration";
import getResultSetLayer from "./result_set_layer";
import { getFieldTrees, TreePositions } from "./trees";
import {
  GetCustomerParams,
  GetResultSetParams,
  GetFieldParams,
  GetResultSetLayerParams,
} from "./route-params.types";
import { AppleCountCalibrationFeatureCollection } from "../routes/customer/_customer_id/field/_field_id/result-set/calibrate/Calibration.types";

export type { ResultSetCalibration };

export type Field = {
  name: string;
  id: number;
  customer_id: number;
  area_ha: number;
  created_at: string;
  updated_at: string;
  has_trees: boolean;
};

export type FieldProperties = {
  name: string;
  remark: string | null;
  location_link: string | null;
  plant_date: `${number}-${number}-${number}` | null;
  crop: string | null;
  variety: string | null;
  planting_distance_meters: number | null;
  location_id: string | null;
  id: number;
  customer_id: number;
  area_ha: number;
};

export type FieldsGeoJSON = FeatureCollection<
  GeoJsonMultiPolygon | GeoJsonPolygon,
  FieldProperties
>;

export type FieldsData = {
  fields: FieldsGeoJSON;
  tilings: Tiling[];
  customer: ApiCustomer;
};

export type ResultSet = {
  id: string;
  product_sku: string;
  field_id: number;
  detection_dates: [`${number}-${number}-${number}`];
  external_visibility: Array<string>;
  driving_day_id?: string;
  created_at: string;
  updated_at: string;
  calibration?: ResultSetCalibration | null;
};

export type Tiling = {
  status: string;
  id: number;
  customer_id: number;
  extent_polygon?: GeoJsonPolygon | null;
  flight_date: `${number}-${number}-${number}` | null;
  created_at: `${number}-${number}-${number}`;
  label: string;
  max_zoom_level?: number;
};

export type CalibrationData = {
  geojson: AppleCountCalibrationFeatureCollection;
  trees: TreePositions;
  resultSet: ResultSet;
};

export async function getFieldsData({
  params: { customerId },
}: GetCustomerParams) {
  const [fields, tilings, customer] = await Promise.all([
    get<FieldsGeoJSON>(`/v1/customer/${customerId}/field/boundary/geojson`),
    get<Tiling[]>(`/v1/customer/${customerId}/tiling`),
    get<ApiCustomer>(`/v1/customer/${customerId}`),
  ]);

  setLogsCustomer(customer);

  return {
    fields,
    tilings,
    customer,
  };
}

export async function getResultSetAndTrees({
  params: { fieldId, resultSetId },
}: GetResultSetParams) {
  const [geojson, trees, resultSet] = await Promise.all([
    getResultSetLayer({ params: { resultSetId } } as GetResultSetLayerParams),
    getFieldTrees({ params: { fieldId } } as GetFieldParams),
    get<ResultSet>(`v1/result_set/${resultSetId}`),
  ]);

  return { geojson, trees: trees.positions, resultSet };
}

export async function getTaskmapHistoryEntries({
  params: { customerId },
}: GetCustomerParams) {
  return get<TaskmapHistoryEntry[]>(`v1/customer/${customerId}/task_map`);
}

export async function getResultSets({
  params: { customerId },
}: GetCustomerParams) {
  return get<ResultSet[]>(
    `/v1/customer/${customerId}/result_set?limit=2000&sort=detection_dates&direction=DESC`,
  );
}

export async function updateFieldProps({
  request,
  params,
}: ActionFunctionArgs): Promise<MessageLevel> {
  const token = await useAuth.getState().getToken();
  const formData = await request.formData();
  const plantDate = formData.get("plantingDate") as string;

  const fieldData = {
    name: formData.get("name") as string,
    crop: formData.get("crop") as string,
    variety: formData.get("variety") as string,
    planting_distance_meters: Number(formData.get("plantDistance") || 0) * 0.01,
    plant_date: plantDate ? new Date(plantDate).toISOString() : undefined,
    remark: formData.get("remarks") as string,
  };

  const response = await fetch(getUrl(`/v1/field/${params.fieldId}`), {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(fieldData),
  });

  return (
    {
      200: MessageLevel.Success,
      422: MessageLevel.Error,
    }[response.status] || MessageLevel.Error
  );
}

export async function updateFieldExtent({
  body,
  fieldId,
}: {
  body: string;
  fieldId: string;
}): Promise<Response> {
  const token = await useAuth.getState().getToken();

  return fetch(getUrl(`/v1/field/${fieldId}`), {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body,
  });
}

export async function saveNewField(
  customerId: number,
  fieldData: {
    name: string;
    crop: string;
    variety: string;
    planting_distance_meters: number;
    plant_date?: string | undefined;
    remark: string;
    boundary: Geometry;
  },
): Promise<Response> {
  const token = await useAuth.getState().getToken();
  return await fetch(getUrl(`/v1/customer/${customerId}/field`), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(fieldData),
  });
}
